<template>
  <v-card outlined class="aveine-bottle-image mb-6">
    <v-card-title class="text-subtitle-1">
      {{$t('intranet.bottleImage.title')}}
    </v-card-title>

    <v-card-subtitle class="pb-2">
      {{$t(inherited ? 'intranet.bottleImage.inherited' : 'intranet.bottleImage.help')}}
    </v-card-subtitle>

    <v-card-text>
      <v-row class="d-flex align-center">
        <v-col cols="12" sm="4" class="d-flex flex-column align-center">
          <img v-if="preview" class="bottle-preview" :alt="$t('intranet.bottleImage.title')" :src="preview">
          <v-icon v-else size="72" color="grey lighten-1">mdi-bottle-wine-outline</v-icon>
        </v-col>

        <v-col cols="12" sm="8">
          <v-btn depressed color="primary" :loading="busy" @click="$refs.input.click()">
            {{$t('intranet.bottleImage.choose')}}
          </v-btn>
          <input ref="input" type="file" class="d-none" accept="image/png,image/jpeg" @change="send">
          <v-btn v-if="ownImage" text class="ml-2" :loading="busy" @click="remove()">
            {{$t('intranet.bottleImage.remove')}}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from 'axios'
import { errorHandler } from 'aveine-toolbox'
import { headers } from '@/store/index'

/**
 * Photo officielle de la bouteille, pour un vin ou pour une cuvée.
 *
 * Composant a part et non un champ de plus dans AveineWineForm : le
 * televersement a sa PROPRE route multipart cote API, il ne part pas avec le
 * PATCH JSON:API du formulaire. Le greffer dans un composant de 2300 lignes,
 * partage avec les autres applications, n'aurait rien rapproche.
 */
export default {
  name: 'AveineBottleImage',
  props: {
    /** `wines` ou `wine-templates`. */
    resource: { type: String, required: true },
    /** L'identifiant de la ressource editee. */
    id: { type: String, required: true },
    APIbaseURL: String
  },
  data () {
    return { current: null, fallback: null, busy: false }
  },
  computed: {
    ownImage () { return !!this.current },
    inherited () { return !this.current && !!this.fallback },
    preview () { return this.current || this.fallback }
  },
  async created () {
    await this.load()
  },
  methods: {
    /**
     * Lit son propre etat plutot que de le recevoir du parent.
     *
     * Les modeles vuex-orm vivent dans aveine-toolbox et n'ont pas de champ
     * `bottle_image` : vuex-orm ecarte a l'insertion tout attribut non declare,
     * donc `wine.bottle_image` serait undefined quoi qu'envoie l'API. Plutot
     * que d'aller modifier la toolbox et de remonter son SHA pour un champ, le
     * composant fait sa propre lecture — il parle deja en direct a l'API pour
     * le televersement.
     */
    async load () {
      try {
        const url = this.resource === 'wines'
          ? `${this.APIbaseURL}/wines/${this.id}?include=template`
          : `${this.APIbaseURL}/wine-templates/${this.id}`
        const { data } = await axios.get(url, { headers: this.authOnly() })
        this.current = data.data.attributes['bottle-image'] || null
        const template = (data.included || []).find(i => i.type === 'wine-templates')
        this.fallback = template ? (template.attributes['bottle-image'] || null) : null
      } catch (errors) {
        errorHandler(errors, this)
      }
    },

    /**
     * Le jeton, et RIEN d'autre.
     *
     * App.vue pose un Content-Type application/vnd.api+json dans les en-tetes
     * partages. Le reprendre ici casserait le televersement : sur un multipart,
     * c'est au navigateur d'ecrire le Content-Type, avec la frontiere qu'il
     * vient de tirer. Un type impose et sans frontiere donne un corps que le
     * serveur ne sait pas decouper.
     */
    authOnly () {
      return { Authorization: headers.Authorization }
    },

    async send (event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file) return

      if (file.size > 5242880) {
        this.$toastr.e(this.$t('intranet.bottleImage.tooBig'))
        return
      }
      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
        this.$toastr.e(this.$t('intranet.bottleImage.wrongType'))
        return
      }

      // FormData a la main : la route attend un multipart d'un seul champ,
      // sans document JSON:API, donc rien a faire passer par le serialiseur de
      // la toolbox.
      const form = new FormData()
      form.append('attachment_image', file)
      this.busy = true
      try {
        const response = await axios.post(
          `${this.APIbaseURL}/${this.resource}/${this.id}/bottle-image?lang=${this.$i18n.locale}`,
          form,
          { headers: this.authOnly() }
        )
        this.current = response.data.data.attributes['bottle-image']
        this.$toastr.s(this.$t('intranet.bottleImage.saved'))
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.busy = false
      }
    },

    async remove () {
      this.busy = true
      try {
        await axios.delete(
          `${this.APIbaseURL}/${this.resource}/${this.id}/bottle-image`,
          { headers: this.authOnly() }
        )
        this.current = null
        this.$toastr.s(this.$t('intranet.bottleImage.removed'))
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .bottle-preview {
    max-height: 220px;
    max-width: 100%;
  }
</style>
